import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import emitter from "@webcore/core/events/eventemitter";
import appEvents from "@core/events/events";
import { IS_EYEJACK_APP } from "@webcore/core/utils/utils";
import { openURL } from "@webcore/core/openurl/openurl";
import { useXRSession } from "@core/hooks/useXRSession";
import { useEJXLaunchUrl } from "@core/hooks/useEJXLaunchUrl";
import { useUrlContentId } from "@core/hooks/useUrlContentId";
import { useXRCheckInline } from "@core/hooks/useXRCheckInline";
import { useArSessionTimer, type ArSessionExitParams } from "@core/hooks/useArSessionTimer";
import { ensureRuntimeReady } from "@core/runtime/runtime";
import type { ModelData } from "@core/content/types";

export enum AppState {
  LANDING = "LANDING",
  TRANSITIONING_XR = "TRANSITIONING_XR",
  AR_EXPERIENCE = "AR_EXPERIENCE",
  APP_CLIP_EXITED = "APP_CLIP_EXITED",
}

export interface UseARExperienceOptions {
  contentData: ModelData[];
  checkRuntimeAppReady?: () => Promise<boolean>;
  prewarmRuntimeOnMount?: boolean;
  onLaunchClick?: (model: ModelData) => void;
  onEnteringXR?: (selectedModel: ModelData | null) => void;
  onExitingXR?: (params: ArSessionExitParams, selectedModel: ModelData | null) => void;
}

export interface UseARExperienceReturn {
  appState: AppState;
  selectedModel: ModelData | null;
  modelReady: boolean;
  runtimeReady: boolean;
  isPreparingRuntime: boolean;
  isPlaced: boolean;
  isRecording: boolean;
  setIsRecording: (value: boolean) => void;
  showLanding: boolean;
  xrIsChecking: boolean;
  xrIsCompatible: boolean;
  xrIsError: boolean;
  xrErrorMessage: string | null;
  handleLaunchClick: (model: ModelData) => void;
  handleLaunchAR: (model: ModelData) => Promise<void>;
  handleExitAR: () => void;
}

export function getBasePath(): string {
  return window.location.pathname || "/";
}

export function useARExperience({
  contentData,
  checkRuntimeAppReady,
  prewarmRuntimeOnMount = true,
  onLaunchClick,
  onEnteringXR,
  onExitingXR,
}: UseARExperienceOptions): UseARExperienceReturn {
  const [appState, setAppState] = useState<AppState>(
    IS_EYEJACK_APP ? AppState.TRANSITIONING_XR : AppState.LANDING
  );
  const [selectedModel, setSelectedModel] = useState<ModelData | null>(null);
  const [contentLoaded, setContentLoaded] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [modelReady, setModelReady] = useState(false);
  const [isPlaced, setIsPlaced] = useState(false);
  const [runtimeReady, setRuntimeReady] = useState(IS_EYEJACK_APP);
  const [isPreparingRuntime, setIsPreparingRuntime] = useState(false);

  const safariReturnUrlRef = useRef<string | null>(null);

  const xrSession = useXRSession({
    isLoaded: contentLoaded,
    isInAppClip: IS_EYEJACK_APP,
    autoStartInAppClip: true,
  });

  const launchUrl = useEJXLaunchUrl();

  // Safari return URL handling for App Clips
  useEffect(() => {
    if (!IS_EYEJACK_APP) return;
    const returnUrl =
      typeof window !== "undefined"
        ? new URLSearchParams(window.location.search).get("returnUrl")
        : null;
    if (returnUrl && returnUrl.startsWith("http")) {
      safariReturnUrlRef.current = returnUrl;
    } else if (launchUrl && launchUrl.startsWith("http")) {
      safariReturnUrlRef.current = launchUrl;
    }
  }, [launchUrl]);

  const validContentIds = useMemo(() => contentData.map((c) => c.id), [contentData]);
  const contentId = useUrlContentId({
    url: launchUrl,
    validIds: validContentIds,
    defaultId: validContentIds[0],
  });

  const { isChecking: xrIsChecking, isCompatible: xrIsCompatible } = useXRCheckInline();

  useArSessionTimer({
    isActive: appState === AppState.AR_EXPERIENCE && modelReady,
    hasExited: xrSession.hasExited,
    onEnteringXR: useCallback(() => {
      onEnteringXR?.(selectedModel);
    }, [onEnteringXR, selectedModel]),
    onExitingXR: useCallback(
      (params: ArSessionExitParams) => {
        onExitingXR?.(params, selectedModel);
      },
      [onExitingXR, selectedModel]
    ),
  });

  // Set initial selected model from URL
  useEffect(() => {
    if (selectedModel) return;
    const model = contentData.find((m) => m.id === contentId) || contentData[0];
    if (model) setSelectedModel(model);
  }, [contentId, selectedModel, contentData]);

  // App Clip: emit model ID when content is loaded
  useEffect(() => {
    if (!IS_EYEJACK_APP || !contentLoaded || !selectedModel) return;
    (emitter as any).emit("set-model-id", selectedModel.id);
  }, [contentLoaded, selectedModel]);

  // Manage AR mode body class
  useEffect(() => {
    if (appState === AppState.AR_EXPERIENCE) {
      document.body.classList.add("ar-mode");
    } else {
      document.body.classList.remove("ar-mode");
    }
  }, [appState]);

  // Transition to AR_EXPERIENCE when XR is running and model is ready
  useEffect(() => {
    if (!xrSession.isRunning) return;
    if (modelReady && (appState === AppState.TRANSITIONING_XR || appState === AppState.LANDING)) {
      setAppState(AppState.AR_EXPERIENCE);
      return;
    }
    if (!modelReady && appState === AppState.LANDING) {
      setAppState(AppState.TRANSITIONING_XR);
    }
  }, [xrSession.isRunning, modelReady, appState]);

  // Handle XR exit
  useEffect(() => {
    if (xrSession.hasExited && appState === AppState.AR_EXPERIENCE) {
      if (IS_EYEJACK_APP) {
        setAppState(AppState.APP_CLIP_EXITED);
        const urlToOpen = safariReturnUrlRef.current || launchUrl;
        openURL(urlToOpen).catch(() => {});
      } else {
        setAppState(AppState.LANDING);
        const base = getBasePath();
        window.history.pushState({}, "", `${base}${window.location.search}${window.location.hash}`);
      }
      setSelectedModel(null);
      setIsPlaced(false);
    }
  }, [xrSession.hasExited, appState, launchUrl]);

  // Listen for runtime events
  useEffect(() => {
    const handleLoaded = () => setContentLoaded(true);
    const handleContentLoaded = () => setModelReady(true);
    const handleContentLoading = () => setModelReady(false);
    const handleContentPositioned = () => setIsPlaced(true);
    const handleRepositioning = () => setIsPlaced(false);

    emitter.on("loaded", handleLoaded);
    emitter.on("content-loaded", handleContentLoaded);
    emitter.on("content-loading", handleContentLoading);
    appEvents.on("content-positioned", handleContentPositioned);
    (emitter as any).on("content-repositioning", handleRepositioning);

    if (checkRuntimeAppReady) {
      checkRuntimeAppReady()
        .then((isReady) => {
          if (isReady) setContentLoaded(true);
        })
        .catch(() => {});
    }

    return () => {
      emitter.off("loaded", handleLoaded);
      emitter.off("content-loaded", handleContentLoaded);
      emitter.off("content-loading", handleContentLoading);
      appEvents.off("content-positioned", handleContentPositioned);
      (emitter as any).off("content-repositioning", handleRepositioning);
    };
  }, [selectedModel, checkRuntimeAppReady]);

  // Prewarm runtime on mount (web only)
  // App Clip loads immediately via configureRuntime, so skip here
  useEffect(() => {
    if (IS_EYEJACK_APP || !prewarmRuntimeOnMount) return;

    setIsPreparingRuntime(true);
    ensureRuntimeReady()
      .then(() => setRuntimeReady(true))
      .catch(() => {})
      .finally(() => setIsPreparingRuntime(false));
  }, [prewarmRuntimeOnMount]);

  // Handle XR errors
  useEffect(() => {
    if (xrSession.isError && appState === AppState.TRANSITIONING_XR) {
      setAppState(AppState.LANDING);
    }
  }, [xrSession.isError, appState]);

  const handleLaunchClick = useCallback((model: ModelData) => {
    onLaunchClick?.(model);
  }, [onLaunchClick]);

  /**
   * Launch AR experience:
   * 1. Wait for runtime (should be prewarmed, so instant)
   * 2. Start XR session (request permissions)
   * 3. Emit model ID to display content
   */
  const handleLaunchAR = useCallback(
    async (model: ModelData) => {
      handleLaunchClick(model);
      setSelectedModel(model);

      const base = getBasePath();
      const params = new URLSearchParams(window.location.search);
      params.set("fromAr", "1");
      window.history.pushState({}, "", `${base}?${params.toString()}#${model.id}`);

      if (IS_EYEJACK_APP) {
        (emitter as any).emit("set-model-id", model.id);
        setAppState(AppState.TRANSITIONING_XR);
        return;
      }

      // Ensure runtime is ready.
      // The runtime may have been disposed during mode switches (e.g. WebXR -> 8w),
      // so validate actual readiness on every launch, not just cached local state.
      let runtimeIsReady = runtimeReady;
      if (checkRuntimeAppReady) {
        try {
          runtimeIsReady = await checkRuntimeAppReady();
          if (runtimeReady !== runtimeIsReady) {
            setRuntimeReady(runtimeIsReady);
          }
        } catch {
          runtimeIsReady = false;
          if (runtimeReady) {
            setRuntimeReady(false);
          }
        }
      }

      if (!runtimeIsReady) {
        setIsPreparingRuntime(true);
        try {
          await ensureRuntimeReady();
          setRuntimeReady(true);
        } finally {
          setIsPreparingRuntime(false);
        }
      }

      // Start XR session and display content
      setIsPlaced(false);
      (emitter as any).emit("set-model-id", model.id);
      xrSession.start();
    },
    [xrSession, runtimeReady, handleLaunchClick, checkRuntimeAppReady]
  );

  const handleExitAR = useCallback(() => {
    xrSession.stop();
  }, [xrSession]);

  // Handle scroll-to-section on landing
  const showLanding = appState === AppState.LANDING;
  useEffect(() => {
    if (!showLanding) return;
    const hash = window.location.hash;
    if (!hash) return;
    const fromAr = new URLSearchParams(window.location.search).get("fromAr");
    const el = document.querySelector(hash);
    if (el) {
      if (fromAr) document.documentElement.classList.add("instant-scroll");
      el.scrollIntoView({ block: "start" });
      if (fromAr) document.documentElement.classList.remove("instant-scroll");
    }
    const cleanupDelay = fromAr ? 0 : 800;
    setTimeout(() => {
      const base = getBasePath();
      const u = new URL(window.location.href);
      u.hash = "";
      u.searchParams.delete("fromAr");
      window.history.replaceState({}, "", base + u.search);
    }, cleanupDelay);
  }, [showLanding]);

  return {
    appState,
    selectedModel,
    modelReady,
    runtimeReady,
    isPreparingRuntime,
    isPlaced,
    isRecording,
    setIsRecording,
    showLanding,
    xrIsChecking,
    xrIsCompatible,
    xrIsError: xrSession.isError,
    xrErrorMessage: xrSession.errorMessage,
    handleLaunchClick,
    handleLaunchAR,
    handleExitAR,
  };
}
